import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Menu } from 'lucide-react';

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About' },
  { href: '/notes', label: 'Notes' },
  // Hidden until there's more published content — re-enable when ready to show
  // { href: '/projects', label: 'Projects' }, // duplicates Home's Featured Work section
  // { href: '/react', label: 'React Guide' },
  // { href: '/python', label: 'Python Guide' },
  // { href: '/blog', label: 'Blog' },
];

const Navbar: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const isActive = (href: string) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        padding: '0 24px',
        background: scrolled || menuOpen ? 'rgba(8,11,16,0.96)' : 'transparent',
        backdropFilter: scrolled || menuOpen ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(255,255,255,0.06)' : '1px solid transparent',
        transition: 'background 0.3s ease, border-color 0.3s ease',
      }}>
        <div style={{
          maxWidth: 1200, margin: '0 auto',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          height: 64,
        }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
            <div style={{
              width: 34, height: 34, borderRadius: 10,
              background: 'linear-gradient(135deg, #00e5c8, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 14, color: '#080b10',
            }}>P</div>
            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, letterSpacing: '-0.01em', color: 'var(--text)' }}>
              Parmeshwar
            </span>
          </Link>

          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }} className="nav-desktop">
            {navLinks.map(link => (
              <Link key={link.href} to={link.href} style={{
                padding: '6px 13px', borderRadius: 8, fontSize: 13, fontWeight: 500,
                color: isActive(link.href) ? 'var(--cyan)' : 'var(--text2)',
                background: isActive(link.href) ? 'rgba(0,229,200,0.08)' : 'transparent',
                border: isActive(link.href) ? '1px solid rgba(0,229,200,0.2)' : '1px solid transparent',
                transition: 'all 0.18s', textDecoration: 'none',
              }}>{link.label}</Link>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <a href="mailto:parmeshwarpanwar777@gmail.com" className="nav-cta" style={{
              padding: '8px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600,
              background: 'var(--cyan)', color: '#080b10', letterSpacing: '0.01em',
              transition: 'opacity 0.2s', textDecoration: 'none',
            }}
            onMouseOver={e => (e.currentTarget.style.opacity = '0.82')}
            onMouseOut={e => (e.currentTarget.style.opacity = '1')}
            >Hire Me</a>

            <button className="nav-burger" onClick={() => setMenuOpen(o => !o)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              style={{
                display: 'none', background: 'none', border: '1px solid var(--border)',
                borderRadius: 8, padding: '6px 8px', cursor: 'pointer', color: 'var(--text)',
                alignItems: 'center', justifyContent: 'center',
              }}>
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </nav>

      <div className="nav-drawer" style={{
        position: 'fixed', top: 64, left: 0, right: 0, bottom: 0, zIndex: 999,
        background: 'rgba(8,11,16,0.98)', backdropFilter: 'blur(24px)',
        padding: '24px', display: 'flex', flexDirection: 'column', gap: 4,
        transform: menuOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)', overflowY: 'auto',
      }}>
        {navLinks.map((link, i) => (
          <Link key={link.href} to={link.href} style={{
            display: 'block', padding: '16px 18px', borderRadius: 12,
            fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700,
            color: isActive(link.href) ? 'var(--cyan)' : 'var(--text)',
            background: isActive(link.href) ? 'rgba(0,229,200,0.08)' : 'transparent',
            border: isActive(link.href) ? '1px solid rgba(0,229,200,0.2)' : '1px solid transparent',
            textDecoration: 'none',
            animation: menuOpen ? `fadeUp 0.35s ease ${i * 0.06}s both` : 'none',
          }}>{link.label}</Link>
        ))}
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          <a href="mailto:parmeshwarpanwar777@gmail.com" style={{
            display: 'block', textAlign: 'center', padding: '14px 18px',
            borderRadius: 12, fontWeight: 700, fontSize: 16,
            background: 'var(--cyan)', color: '#080b10', textDecoration: 'none',
          }}>Hire Me →</a>
        </div>
      </div>

      <style>{`
        @media (max-width: 860px) {
          .nav-desktop { display: none !important; }
          .nav-burger  { display: flex !important; }
        }
        @media (min-width: 861px) {
          .nav-drawer  { display: none !important; }
        }
      `}</style>
    </>
  );
};

export default Navbar;
